import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Skeleton,
} from "@ncoder/ui";
import {
  MetadataItemRow,
  type MetadataItem,
} from "../../atoms/security/MetadataItemRow";
import {
  ChevronLeft,
  ChevronRight,
  FileCode,
  AlertTriangle,
} from "lucide-react";

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

interface FindingsTableProps {
  metadataItems: MetadataItem[];
  isLoading?: boolean;
  searchTerm: string;
  selectedSeverity: string;
  selectedCategory: string;
  severities?: string[];
  categories?: string[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
  startItem: number;
  endItem: number;
  totalFindings: number;
  totalMetadataItems?: number;
  onSearchChange: (value: string) => void;
  onSeverityChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onImproveWithAI?: (item: MetadataItem) => void;
  isScanning?: boolean;
}

export const FindingsTable: React.FC<FindingsTableProps> = ({
  metadataItems,
  isLoading = false,
  searchTerm,
  selectedSeverity,
  selectedCategory,
  severities = ["Critical", "High", "Medium", "Low"],
  categories = [
    "BestPractices",
    "CodeStyle",
    "Design",
    "Documentation",
    "Security",
  ],
  currentPage,
  totalPages,
  pageSize,
  startItem,
  endItem,
  totalFindings,
  totalMetadataItems,
  onSearchChange,
  onSeverityChange,
  onCategoryChange,
  onPageChange,
  onPageSizeChange,
  onImproveWithAI,
  isScanning,
}) => {
  // Track which items are expanded
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedItems(
      new Set(metadataItems.map((item) => item.metadata_item_id)),
    );
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  // Calculate summary stats
  const totalFindingsInView = metadataItems.reduce(
    (sum, item) => sum + item.findings_count,
    0,
  );

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="flex-shrink-0 pb-4">
        {/* Single Row: Title + Filters */}
        <div className="flex flex-wrap gap-2">
          {/* Title */}
          <CardTitle className="flex items-center gap-2 flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Security Findings
          </CardTitle>

          {/* Filters - inline with title */}
          <div className="flex items-center gap-2 flex-1">
            <Input
              placeholder="Search files, rules..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="min-w-[140px] flex-1"
            />
            <Select value={selectedSeverity} onValueChange={onSeverityChange}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                {severities.map((severity) => (
                  <SelectItem key={severity} value={severity}>
                    {severity}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={onCategoryChange}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Expand/Collapse Links */}
          {metadataItems.length > 0 && (
            <div className="flex items-center gap-1 text-sm flex-shrink-0">
              <button
                onClick={expandAll}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Expand All
              </button>
              <span className="text-muted-foreground">|</span>
              <button
                onClick={collapseAll}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Collapse All
              </button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-auto p-0">
        {isLoading ? (
          <div className="space-y-1 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4 border-b">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-5 w-5" />
                <div className="flex-1">
                  <Skeleton className="h-5 w-48 mb-1" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-5 w-16" />
              </div>
            ))}
          </div>
        ) : metadataItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <FileCode className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">No findings found</p>
            <p className="text-sm">
              Try adjusting your filters or run a new scan
            </p>
          </div>
        ) : (
          <div className="divide-y-0">
            {metadataItems.map((item) => (
              <MetadataItemRow
                key={item.metadata_item_id}
                item={item}
                isExpanded={expandedItems.has(item.metadata_item_id)}
                onToggle={toggleItem}
                onImproveWithAI={onImproveWithAI}
                isScanning={isScanning}
              />
            ))}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex-shrink-0 border-t pt-4">
        {totalFindings > 0 && (
          <div className="flex flex-wrap items-center justify-between w-full gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Showing {startItem}-{endItem} of{" "}
                {totalMetadataItems || metadataItems.length} files (
                {totalFindingsInView} issues)
              </span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => onPageSizeChange(Number(value))}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">per page</span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || isLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

export default FindingsTable;
